import { Badge, Flex, IconButton, Text, Tooltip } from '@chakra-ui/react';
import { FiEye } from 'react-icons/fi';
import { Apontamento } from '../service/apontamentos';
import { dataHoraIsoParaBr } from '../helpers/dataBr';

/** Suplen (0101) e NeuroVasc (0301) atendem o mesmo hospital. */
export const FILIAL_CFG: Record<string, { label: string; cor: string }> = {
  '0101': { label: 'SUPLEN', cor: 'blue' },
  '0301': { label: 'NEUROVASC', cor: 'purple' },
};

export function getColumn(verApontamento: (apontamento: Apontamento) => void) {
  const columns = [
    {
      Header: 'Apontado em',
      accessor: 'data_cadastro',
      Cell: ({ row }: any) => {
        // data_cadastro é timestamp (UTC no banco): converte pro fuso local em vez
        // de fatiar a string, senão a hora aparece 3h adiantada.
        const { data, hora } = dataHoraIsoParaBr(row.original.data_cadastro);
        return (
          <Flex direction="column">
            <Text fontSize="13px" fontWeight="600">{data || '-'}</Text>
            <Text fontSize="11px" color="gray.400">{hora}</Text>
          </Flex>
        );
      },
    },
    {
      Header: 'Hospital',
      accessor: 'hospital',
      Cell: ({ row }: any) => (
        <Text fontSize="13px" fontWeight="600">
          {row.original.clientes?.fantasia || row.original.clientes?.razao_social || '-'}
        </Text>
      ),
    },
    {
      Header: 'Paciente',
      accessor: 'paciente',
      Cell: ({ row }: any) => (
        <Text fontSize="13px">{row.original.paciente || '-'}</Text>
      ),
    },
    {
      Header: 'Médico',
      accessor: 'medico_nome',
      Cell: ({ row }: any) => (
        <Text fontSize="13px">{row.original.medico_nome || '-'}</Text>
      ),
    },
    {
      Header: 'Convênio',
      accessor: 'convenio_nome',
      Cell: ({ row }: any) => (
        <Text fontSize="13px">{row.original.convenio_nome || '-'}</Text>
      ),
    },
    {
      Header: 'Vendedor',
      accessor: 'vendedor',
      Cell: ({ row }: any) => (
        <Text fontSize="13px">{row.original.usuarios?.name || '-'}</Text>
      ),
    },
    {
      // Rótulo "Empresa" (pedido do usuário, 2026-09-09): o dado continua sendo
      // o código de filial do ERP (0101/0301), mas a operação chama de empresa.
      Header: 'Empresa',
      accessor: 'filial',
      Cell: ({ row }: any) => {
        const cfg = FILIAL_CFG[row.original.filial];
        return (
          <Badge colorScheme={cfg?.cor || 'gray'} fontSize="10px">
            {cfg?.label || row.original.filial}
          </Badge>
        );
      },
    },
    {
      Header: 'Itens',
      accessor: 'itens',
      Cell: ({ row }: any) => {
        const etiquetas = row.original._count?.etiquetas ?? 0;
        const anexos = row.original._count?.anexos ?? 0;
        return (
          <Flex gap="6px" align="center">
            <Badge colorScheme="gray" fontSize="10px">{etiquetas} etiq.</Badge>
            {anexos > 0 && <Badge colorScheme="green" fontSize="10px">{anexos} anexo(s)</Badge>}
            {/* Apontamento feito pelo atalho da home, sem cirurgia agendada. */}
            {!row.original.agendamento_id && (
              <Badge colorScheme="orange" fontSize="10px">AVULSO</Badge>
            )}
          </Flex>
        );
      },
    },
    {
      Header: 'Visualizar',
      accessor: 'acoes',
      // Coluna de ação: não existe dado por trás, então nada de ordenar/filtrar.
      disableSortBy: true,
      disableFilters: true,
      Cell: ({ row }: any) => (
        <Tooltip label="Ver materiais e anexos">
          <IconButton
            aria-label="Ver apontamento"
            icon={<FiEye />}
            size="sm"
            variant="ghost"
            onClick={() => verApontamento(row.original)}
          />
        </Tooltip>
      ),
    },
  ];

  return columns;
}
